import React, { useCallback, useEffect, useState } from 'react'

import { where } from 'firebase/firestore'
import { cloneDeep } from 'lodash'
import { useParams } from 'react-router'

import ViewsLoader from '../../../components/loader/ViewsLoader'
import { TicketsColumn } from '../../../components/common/TicketsColumn'
import SpectatorTickesPurchaseWrapper from '../SpectatorTickesPurchaseWrapper'

import helpers from '../../../commonHelpers/helpers'
import { getUserFullName } from '../../../helpers/helpers'
import { getConvertedData } from '../../../models/interface.helper'
import { getCurrency } from '../../../helpers/payment'

import FirestoreService from '../../../services/firestoreService'
import useToasterHelper from '../../../helpers/ToasterHelper'
import useGuest from '../../../hooks/useGuest'
import useGetEventData from '../../../hooks/users/common/useGetEventData'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectEventTicketsR,
  selectSpectatorTickets,
  setEventTickets,
  setSpectatorEventData,
} from '../../../store/tickets/ticketslice'
import { selectIsLoggedIn, selectUserId, selectUserReducer } from '../../../store/user/userSlice'

import { IEventDetails } from '../../../models/event-details/event-details.interface'
import { TicketType } from '../../../models/event-ticketing/event-ticketing.interface'
import { SpectatorTicketModel } from '../../../models/spectator-tickets/spectator-tickets-model'
import { ISpectatorTickets } from '../../../models/spectator-tickets/spectator-tickets.interface'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'

import { CONST } from '../../../const/const'
import { MESSAGES_CONST } from '../../../const/messages-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

const SpeactatorTicketsPurchaseTicketsTab = ({
  saveRef,
  nextRef,
  onSetEventTab,
  props,
  ...rest
}: {
  EventDetails: IEventDetails
  eventTickets: TicketType[]
  saveRef: any
  nextRef: any
  props: any
  onSetEventTab: any
  setLoading: any
}) => {
  const dispatch = useAppDispatch()
  const guestHook = useGuest()
  const guest_id = guestHook.guestId
  const toastFunctions = useToasterHelper()
  const userId = useAppSelector(selectUserId)
  const user = useAppSelector(selectUserReducer)

  const eventId = useParams<{ id: string }>()?.id
  const isLoggedIn = useAppSelector(selectIsLoggedIn)

  const [loading, setLoading] = useState(true)
  const eventTickets = useAppSelector(selectEventTicketsR)
  const spectatorTickets = useAppSelector(selectSpectatorTickets)

  const { getAllData } = useGetEventData()

  const [eventData, setEventData] = useState<IEventReviewPublish | null>(null)

  const currency = getCurrency(eventData?.EventPaymentSettings?.currency)

  useEffect(() => {
    if (eventId) {
      setLoading(true)
      getAllData(eventId, ['v01_event_review-publish'])
        .then((data) => {
          if (data['v01_event_review-publish']?.EventTickets?.sellSpectatorTickets) {
            dispatch(
              setEventTickets(
                (data['v01_event_review-publish']?.EventTickets.tickets ?? []).map((currTicket) => {
                  return getConvertedData(currTicket)
                })
              )
            )
          }

          setEventData(data['v01_event_review-publish'])
        })
        .catch((error) => {
          toastFunctions.error({
            message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
          })
          console.log(error)
        })
    }
  }, [dispatch, eventId, getAllData, setLoading, toastFunctions])

  // Functions

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @returns Add and update tickets
   */
  const handleSave = useCallback(async () => {
    console.log('handleSave')
  }, [])

  /**
   * @returns get tickets firebase function
   */
  useEffect(() => {
    const getSpectatorTickets = async () => {
      let eventTickets_ = [...eventTickets]
      let spectatorTicket: ISpectatorTickets | null = null
      let spectatorTicketsMap: Map<string, ISpectatorTickets> = new Map()

      const spectatorTicketSnapshots = await FirestoreService.filterItems(
        COLLECTIONS.SPECTATOR_TICKETS.NAME,
        [
          where(COLLECTIONS.SPECTATOR_TICKETS.FIELDS.EVENT_ID, '==', eventId),
          where(COLLECTIONS.SPECTATOR_TICKETS.FIELDS.USER_ID, '==', isLoggedIn ? userId : guest_id),
          where(
            COLLECTIONS.SPECTATOR_TICKETS.FIELDS.PAYMENT_STATUS.KEY,
            '!=',
            COLLECTIONS.SPECTATOR_TICKETS.FIELDS.PAYMENT_STATUS.VALUES.PAID
          ),
        ]
      )

      if (spectatorTicketSnapshots.size === 0) {
        return null
      } else {
        const spectatorTickets = spectatorTicketSnapshots.docs.map((currDoc) => {
          spectatorTicket = getConvertedData(
            SpectatorTicketModel.fromFirestoreDoc(currDoc).toObject()
          )
          if (spectatorTicket.ticketItemId)
            spectatorTicketsMap.set(spectatorTicket.ticketItemId, spectatorTicket)
          return spectatorTicket
        })

        dispatch(
          setSpectatorEventData(
            getConvertedData({
              spectator_tickets: spectatorTickets,
            })
          )
        )

        eventTickets_ = eventTickets_.map((currEventTicket) => {
          return {
            ...currEventTicket,
            spectatorTicket: spectatorTicketsMap.get(currEventTicket?.uuid) ?? null,
          }
        })

        dispatch(setEventTickets(getConvertedData(eventTickets_)))
      }
    }

    try {
      if ((userId || guest_id) && eventTickets.length) {
        setLoading(true)
        getSpectatorTickets()
      }
    } catch (error) {
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
      console.log(error)
    } finally {
      setLoading(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isLoggedIn, guest_id, eventTickets.length])

  /**
   * @returns save data and open next tab
   */
  const handleNext = useCallback(async () => {
    let updatedSpectatorTickets: ISpectatorTickets[] = []
    let spectatorTicketToUpdate: ISpectatorTickets[] = []
    let spectatorTicketToCreate: ISpectatorTickets[] = []

    rest.setLoading(true)

    try {
      const ticketUnitsSelected = spectatorTickets.find((currTicket) => {
        return !!currTicket.selectedUnitsCount
      })

      if (!ticketUnitsSelected) {
        return toastFunctions.info({ message: MESSAGES_CONST.NO_SPECTATOR_TICKET_SELECTED })
      }

      spectatorTickets.forEach((currSpectatorTicket) => {
        if (!!currSpectatorTicket.id && currSpectatorTicket.update) {
          spectatorTicketToUpdate.push({
            ...new SpectatorTicketModel({
              ...currSpectatorTicket,
              recipientProfilePicture: user.profileDetails?.userProfilePicture ?? null,
              userName: user.profileDetails ? getUserFullName(user.profileDetails) : '',
            }).toFirestore(),
            id: currSpectatorTicket.id,
          })
        } else if (!currSpectatorTicket.id) {
          spectatorTicketToCreate.push(
            new SpectatorTicketModel({
              ...currSpectatorTicket,
              recipientProfilePicture: user.profileDetails?.userProfilePicture ?? null,
              userName: user.profileDetails ? getUserFullName(user.profileDetails) : '',
            }).toFirestore()
          )
        } else {
          updatedSpectatorTickets.push(currSpectatorTicket)
        }
      })

      // Update - SpectatorTickets

      await helpers.asyncWhileLoop({
        loopCount: spectatorTicketToUpdate.length,
        functionToFire: async (index) => {
          await FirestoreService.updateItem(
            COLLECTIONS.SPECTATOR_TICKETS.NAME,
            spectatorTicketToUpdate[index].id!,
            spectatorTicketToUpdate[index]
          )
        },
      })

      // Create - SpectatorTickets

      await helpers.asyncWhileLoop({
        loopCount: spectatorTicketToCreate.length,
        functionToFire: async (index) => {
          spectatorTicketToCreate[index].id = (
            await FirestoreService.createItem(
              COLLECTIONS.SPECTATOR_TICKETS.NAME,
              spectatorTicketToCreate[index]
            )
          ).id
        },
      })

      updatedSpectatorTickets = [
        ...updatedSpectatorTickets,
        ...spectatorTicketToCreate,
        ...spectatorTicketToUpdate,
      ]

      updatedSpectatorTickets = updatedSpectatorTickets.map((currSpectatorTicket) => {
        return getConvertedData(currSpectatorTicket)
      })

      dispatch(
        setSpectatorEventData({
          spectator_tickets: updatedSpectatorTickets,
        })
      )

      onSetEventTab?.(CONST.UI.SPECTATOR_TABS.TABS.EMAIL)
    } catch (error) {
      console.log(error)
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      rest.setLoading(false)
    }
  }, [dispatch, onSetEventTab, rest, spectatorTickets, toastFunctions, user.profileDetails])

  useEffect(() => {
    if (eventId && (!!guest_id || !!userId)) {
      saveRef.current = handleSave
      nextRef.current = handleNext
    }
  }, [eventId, guest_id, handleNext, handleSave, nextRef, saveRef, userId])

  const hadleTicketUpdate = (newValue: any, ticketIndex: number) => {
    let spectatorTicketIndex = -1
    let eventTickets_ = [...eventTickets]
    let spectatorTickets_ = [...spectatorTickets]
    let eventTicket = cloneDeep(eventTickets_[ticketIndex])

    let valueToReturn: ISpectatorTickets = new SpectatorTicketModel().toObject()

    if (!newValue?.value) {
      return console.log(`newValue is ${newValue}`)
    }

    spectatorTicketIndex = spectatorTickets.findIndex((currSpectatorTicket) => {
      return currSpectatorTicket.ticketItemId === eventTicket.uuid
    })

    if (spectatorTicketIndex === -1) {
      eventTicket.spectatorTicket = getConvertedData({
        ...valueToReturn,
        create: true,
        eventDocId: eventId,
        isPlatformUser: !!isLoggedIn,
        ticketPrice: eventTicket.cost,
        ticketTitle: eventTicket.name,
        ticketItemId: eventTicket.uuid,
        userId: isLoggedIn ? userId : guest_id,
        selectedUnitsCount: Number(newValue.value),
        userEmailId: user.profileDetails?.userEmail ?? null,
      })
      spectatorTickets_.push(eventTicket.spectatorTicket)
    } else {
      eventTicket.spectatorTicket = {
        ...spectatorTickets_[spectatorTicketIndex],
        selectedUnitsCount: Number(newValue.value),
        update: !!spectatorTickets_[spectatorTicketIndex].id,
      }

      spectatorTickets_[spectatorTicketIndex] = eventTicket.spectatorTicket
    }

    eventTickets_[ticketIndex] = eventTicket
    dispatch(
      setSpectatorEventData({
        spectator_tickets: spectatorTickets_,
      })
    )
    dispatch(setEventTickets(eventTickets_))
  }

  const onBlur = (value: number, index: number) => {
    hadleTicketUpdate({ value: value || 1 }, index)
  }

  return (
    <SpectatorTickesPurchaseWrapper title="Tickets" description="Select tickets to purchase">
      {loading ? (
        <div className="flex justify-center pt-[40px]">
          <ViewsLoader color="#ff2d55" size="lg" />
        </div>
      ) : eventTickets?.[0] ? (
        eventTickets.map((ticket, index) => (
          <TicketsColumn
            key={index}
            currency={currency}
            name={ticket.name}
            price={ticket.cost}
            available={ticket.available - ticket.sold}
            description={ticket.note}
            defaultValue={ticket?.spectatorTicket?.selectedUnitsCount || 0}
            onBlur={(value) => onBlur(value, index)}
            loading={loading}
          />
        ))
      ) : (
        <div className="text-[14px] text-SeabiscuitLightParagraphTextColor mt-2">
          No tickets has been added to this event.
        </div>
      )}
    </SpectatorTickesPurchaseWrapper>
  )
}

export default SpeactatorTicketsPurchaseTicketsTab
